/* eslint-disable no-param-reassign */
// Radio values match ConcessionAgeRange / ConcessionStudentStatus enum names from ASP.NET Core.
const CHILD_AGE_VALUES = new Set([
  'Age0To4', // 0 to 4
  'Age5To15', // 5 to 15
]);
const NOT_STUDENT_VALUES = new Set([
  'NotAStudent',
]);
const STUDENT_STATUS_VALUES = new Set([
  'SecondaryStudentInAuckland',
  'TertiaryStudentInAuckland',
]);

const STEPS = {
  age: 'concession-calculator-step-age',
  student: 'concession-calculator-step-student',
  fullTime: 'concession-calculator-step-full-time',
  serviceCard: 'concession-calculator-step-service-card',
  submit: 'concession-calculator-step-submit',
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function scrollStepIntoView(stepEl) {
  if (!stepEl || prefersReducedMotion()) return;
  stepEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function scrollChildPathIntoView(submitStepEl) {
  if (!submitStepEl || prefersReducedMotion()) return;
  // Align the button to the bottom of the viewport so the age question stays visible above.
  submitStepEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
}

function clearFieldset(fieldset) {
  if (!fieldset) return;

  fieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
    input.checked = false;
  });
}

function hideStep(stepEl, { clear = true } = {}) {
  if (!stepEl) return;
  stepEl.hidden = true;

  stepEl.querySelectorAll('fieldset').forEach((fieldset) => {
    if (clear) {
      clearFieldset(fieldset);
    }
    fieldset.disabled = true;
  });
}

function showStep(stepEl, { scroll = true } = {}) {
  if (!stepEl) return;
  const wasHidden = stepEl.hidden;
  stepEl.hidden = false;
  stepEl.querySelectorAll('fieldset').forEach((fieldset) => {
    fieldset.disabled = false;
  });
  if (scroll && wasHidden) scrollStepIntoView(stepEl);
}

function getGroupValue(form, name) {
  const checked = form.querySelector(`input[type="radio"][name="${name}"]:checked`);
  return checked ? checked.value : undefined;
}

function isChildAge(ageValue) {
  return CHILD_AGE_VALUES.has(ageValue);
}

function isNotStudent(studentStatus) {
  return studentStatus !== undefined && NOT_STUDENT_VALUES.has(studentStatus);
}

function isEnrolledStudent(studentStatus) {
  return studentStatus !== undefined && STUDENT_STATUS_VALUES.has(studentStatus);
}

function shouldShowStudent(ageValue) {
  return ageValue && !isChildAge(ageValue);
}

function shouldShowFullTime(ageValue, studentStatus) {
  return shouldShowStudent(ageValue) && isEnrolledStudent(studentStatus);
}

function shouldShowServiceCard(ageValue, studentStatus, isEnrolledFullTime) {
  if (!ageValue || isChildAge(ageValue)) return false;

  if (studentStatus === undefined) {
    return false;
  }

  if (isNotStudent(studentStatus)) {
    return true;
  }

  return isEnrolledFullTime !== undefined;
}

function shouldShowSubmit(ageValue, studentStatus, isEnrolledFullTime) {
  if (!ageValue) return false;

  if (isChildAge(ageValue)) {
    return true;
  }

  if (studentStatus === undefined) {
    return false;
  }

  if (isNotStudent(studentStatus)) {
    return true;
  }

  return isEnrolledFullTime !== undefined;
}

function updateFlow(form) {
  const ageValue = getGroupValue(form, 'AgeRange');
  const studentStatus = getGroupValue(form, 'StudentStatus');
  const isEnrolledFullTime = getGroupValue(form, 'IsEnrolledFullTime');

  const stepAge = document.getElementById(STEPS.age);
  const stepStudent = document.getElementById(STEPS.student);
  const stepFullTime = document.getElementById(STEPS.fullTime);
  const stepServiceCard = document.getElementById(STEPS.serviceCard);
  const stepSubmit = document.getElementById(STEPS.submit);

  const showStudent = shouldShowStudent(ageValue);
  const showFullTime = shouldShowFullTime(ageValue, studentStatus);
  const showServiceCard = shouldShowServiceCard(ageValue, studentStatus, isEnrolledFullTime);
  const showSubmit = shouldShowSubmit(ageValue, studentStatus, isEnrolledFullTime);

  showStep(stepAge, { scroll: false });

  if (showStudent) {
    showStep(stepStudent, { scroll: true });
  } else {
    hideStep(stepStudent);
  }

  if (showFullTime) {
    showStep(stepFullTime, { scroll: true });
  } else {
    hideStep(stepFullTime);
  }

  if (showServiceCard) {
    const scrollToServiceCard = isNotStudent(studentStatus) || isEnrolledFullTime !== undefined;
    showStep(stepServiceCard, { scroll: scrollToServiceCard });
  } else {
    hideStep(stepServiceCard);
  }

  if (showSubmit) {
    const submitWasHidden = stepSubmit.hidden;
    showStep(stepSubmit, { scroll: false });
    if (submitWasHidden && isChildAge(ageValue)) {
      scrollChildPathIntoView(stepSubmit);
    }
  } else {
    hideStep(stepSubmit);
  }
}

function isServiceCardCheckbox(input) {
  return (
    input instanceof HTMLInputElement
    && input.type === 'checkbox'
    && Boolean(input.closest(`#${STEPS.serviceCard}`))
  );
}

function bindFlow(form) {
  form.addEventListener('change', (event) => {
    updateFlow(form);

    if (isServiceCardCheckbox(event.target)) {
      const stepSubmit = document.getElementById(STEPS.submit);
      if (stepSubmit && !stepSubmit.hidden) {
        scrollChildPathIntoView(stepSubmit);
      }
    }
  });
  updateFlow(form);
}

function prepareHiddenStepsForSubmit(form) {
  form.querySelectorAll('[data-cc-step]').forEach((stepEl) => {
    if (stepEl.hidden) {
      stepEl.querySelectorAll('fieldset').forEach((fieldset) => {
        clearFieldset(fieldset);
        fieldset.disabled = true;
      });
    }
  });
}

export default function initConcessionCalculatorFlow(form) {
  if (!form) return { prepareHiddenStepsForSubmit: () => {} };

  bindFlow(form);

  return {
    prepareHiddenStepsForSubmit: () => prepareHiddenStepsForSubmit(form),
  };
}
